import React, { useCallback, useEffect, useState } from 'react';

import { useAuth } from 'hooks/useAuth';
import { useToast } from 'hooks/useToast';
import { useResponsiveScreenSizes } from 'hooks/useResponsiveScreenSizes';

import { GuidelinesContainer } from 'components/layout/GuidelinesContainer';
import { UpdateEmail } from 'components/layout/UpdateEmail';
import { Button } from 'components/ui/Button';

import { colors, Fonts, spacing, transparencies } from 'styles/theme';
import {
    Card,
    Bumper,
    ContentScroller,
    NestedCard,
    HorizontalFlexWrapper,
} from 'styles/shared';

export const PursuitResources = () => {
    const { signOut, userId } = useAuth();
    const { showSuccessToast, showErrorToast } = useToast();
    const { isMobileScreenSize } = useResponsiveScreenSizes();

    const [isLoaded, setIsLoaded] = useState<boolean>(false);

    useEffect(() => {
        setIsLoaded(true);
    }, []);

    const onCopyReferralLink = useCallback(async () => {
        try {
            await navigator.clipboard.writeText(
                `https://home.thegrand.world/sign-up/pursuit`
            );
            showSuccessToast('Referral link copied to clipboard!');
        } catch (error) {
            if (typeof error === 'string') {
                showErrorToast(error.toUpperCase());
            } else if (error instanceof Error) {
                showErrorToast(error.message);
            }
        }
    }, [showErrorToast, showSuccessToast]);

    const onCopyProfileLink = useCallback(async () => {
        try {
            await navigator.clipboard.writeText(
                `https://home.thegrand.world/pursuit/directory?modalContent=profile&profileId=${userId}`
            );
            showSuccessToast('Profile link copied to clipboard!');
        } catch (error) {
            if (typeof error === 'string') {
                showErrorToast(error.toUpperCase());
            } else if (error instanceof Error) {
                showErrorToast(error.message);
            }
        }
    }, [showErrorToast, showSuccessToast, userId]);

    const onSignOut = useCallback(() => {
        signOut?.();
    }, [signOut]);

    return (
        <ContentScroller>
            <Bumper $minHeight={spacing.xxxLarge} $shouldHideOnMobile />
            <Bumper $minHeight={spacing.xxxLarge} $shouldHideOnMobile />
            <Bumper $minHeight={spacing.xxSmall} $shouldHideOnDesktop />
            <Card $backgroundColor={transparencies.white95}>
                {isMobileScreenSize ? (
                    <Fonts.Heading6>{`Resources`}</Fonts.Heading6>
                ) : (
                    <Fonts.Heading5>{`Resources`}</Fonts.Heading5>
                )}
                <Bumper />
                {isLoaded ? (
                    <>
                        <NestedCard>
                            <Fonts.Large>Refer a Friend</Fonts.Large>
                            <Bumper $minHeight={spacing.small} />
                            <Fonts.Small $color={colors.grandGreen}>
                                Share this link and save $500 for each applicant
                                who is accepted into The Grand Pursuit Community
                                and lists you as their referral.
                            </Fonts.Small>
                            <Bumper />
                            <HorizontalFlexWrapper>
                                <Button onClick={onCopyReferralLink}>
                                    Copy Referral Link
                                </Button>
                            </HorizontalFlexWrapper>
                        </NestedCard>
                        <Bumper $minHeight={spacing.xLarge} />

                        <NestedCard $padding={spacing.large}>
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                }}
                            >
                                <Fonts.Large>
                                    The Grand Pursuit on Slack
                                </Fonts.Large>
                                <Bumper $minHeight={spacing.small} />
                                <Fonts.Small $color={colors.grandGreen}>
                                    Join our Slack community to connect with
                                    fellow members, share insights, and stay
                                    updated on retreat activities.
                                </Fonts.Small>
                                <Bumper $minHeight={spacing.xSmall} />
                                <Fonts.Small $color={colors.grandGreen}>
                                    If you haven&apos;t joined yet, please use
                                    the invite link provided in your welcome
                                    email to sign up for our Slack workspace.
                                </Fonts.Small>
                                <Bumper />
                                <HorizontalFlexWrapper>
                                    <Button
                                        onClick={() =>
                                            window.open(
                                                'https://thegrandpursuit.slack.com',
                                                '_blank'
                                            )
                                        }
                                        $style="fill"
                                    >
                                        Join Slack Community
                                    </Button>
                                </HorizontalFlexWrapper>
                            </div>
                        </NestedCard>

                        <Bumper $minHeight={spacing.xLarge} />

                        <NestedCard $padding={spacing.large}>
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: spacing.medium,
                                }}
                            >
                                <Fonts.Large $color={colors.grandGreen}>
                                    Account
                                </Fonts.Large>
                                <HorizontalFlexWrapper>
                                    <Button onClick={onCopyProfileLink}>
                                        Copy Profile Link
                                    </Button>
                                </HorizontalFlexWrapper>
                                <UpdateEmail />
                                <HorizontalFlexWrapper>
                                    <Button
                                        onClick={onSignOut}
                                        $style="outline"
                                    >
                                        Log Out
                                    </Button>
                                </HorizontalFlexWrapper>
                            </div>
                        </NestedCard>
                    </>
                ) : null}
            </Card>

            <Bumper />
            <Bumper />
            <Card $backgroundColor={transparencies.white95}>
                <GuidelinesContainer guidelinesTitle="How We Show Up" />
            </Card>
            <Bumper />
            <Bumper />
            <Card $backgroundColor={transparencies.white95}>
                <GuidelinesContainer guidelinesTitle="Code of Conduct" />
            </Card>

            <Bumper $shouldHideOnMobile />
            <Bumper $shouldHideOnMobile />
            <Bumper />
        </ContentScroller>
    );
};
